/**
 * iter74 — Per-app Firebase configuration panel.
 *
 * Replaces the iter73 whole-tenant panel with two independent sub-blocks
 * (Customer + Rider) so a tenant can be in MIXED mode — one app on its
 * own existing Firebase project, the other auto-provisioned. Auto-fetch
 * from the Firebase Management API reduces upload friction to just the
 * Service Account JSON + App IDs.
 *
 *  Exports:
 *    - TenantFirebasePanel (default) — collapsible panel for tenant cards.
 *    - FirebaseAppExistingForm — single-app form used in panel + create modal.
 *    - FirebaseStatusBadge — compact aggregate badge for card headers.
 *    - EMPTY_APP_EXISTING — initial state for the per-app form.
 *    - clientValidateApp — client-side validator (mirrors backend).
 */
import { useEffect, useState } from "react";
import {
    Flame, Loader2, ShieldAlert, Check, X, AlertCircle,
    Sparkles, FileJson, Smartphone, Bike,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";


export const EMPTY_APP_EXISTING = {
    android_package_name: "",
    ios_bundle_id: "",
    service_account_json: "",
    mobile_app_name: "",
    google_services_json: "",
    ios_plist: "",
    auto_fetch: true,
};


/** Cheap client-side gate before the round-trip. Server is authoritative. */
export function clientValidateApp(form) {
    const errs = [];
    const rd = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;
    if (!(form.android_package_name || "").trim()) errs.push("Android package name is required");
    else if (!rd.test(form.android_package_name.trim())) errs.push("Android package name must be reverse-domain (e.g. com.acme.app)");
    if (!(form.ios_bundle_id || "").trim()) errs.push("iOS bundle ID is required");
    else if (!rd.test(form.ios_bundle_id.trim())) errs.push("iOS bundle ID must be reverse-domain (e.g. com.acme.app)");
    if (!(form.service_account_json || "").trim()) errs.push("Service account JSON is required");
    else { try { JSON.parse(form.service_account_json); } catch { errs.push("Service account JSON is not valid JSON"); } }
    if (!form.auto_fetch) {
        if (!(form.google_services_json || "").trim()) errs.push("google-services.json is required when auto-fetch is disabled");
        if (!(form.ios_plist || "").trim()) errs.push("GoogleService-Info.plist is required when auto-fetch is disabled");
    }
    return errs;
}


function FileTextInput({ label, sublabel, value, onChange, accept, testid, rows = 3, optional }) {
    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const txt = await f.text();
        onChange(txt);
    };
    return (
        <div data-testid={testid}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                {label} {optional && <span className="text-muted-foreground/70 normal-case font-normal">(optional · auto-fetched)</span>}
            </p>
            {sublabel && (
                <p className="text-[10px] text-muted-foreground mb-1">{sublabel}</p>
            )}
            <input
                type="file" accept={accept} onChange={onFile}
                className="block text-[11px] mb-1.5"
                data-testid={`${testid}-file`}
            />
            <textarea
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                placeholder={`Or paste the ${label} content here`}
                className="w-full font-mono text-[10px] px-2 py-1.5 rounded border border-border bg-card"
                data-testid={`${testid}-text`}
            />
        </div>
    );
}


export function FirebaseAppExistingForm({ value, onChange, app, tenant }) {
    /** Per-app EXISTING form (Customer OR Rider). ``app`` is the
     *  string label used in placeholders + test IDs. */
    const setField = (k, v) => onChange({ ...value, [k]: v });
    const appLabel = app === "customer" ? "Customer" : "Rider";
    const namePh = app === "customer"
        ? (tenant?.name || "Acme Foods")
        : `${tenant?.name || "Acme Foods"} Driver`;
    return (
        <div className="space-y-3" data-testid={`fb-app-form-${app}`}>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                        Android package name
                    </p>
                    <input
                        data-testid={`fb-${app}-android-pkg`}
                        value={value.android_package_name}
                        onChange={(e) => setField("android_package_name", e.target.value.trim())}
                        placeholder={`com.acme.${app}`}
                        className="w-full px-2 py-1.5 text-xs border border-border rounded-md bg-card font-mono"
                    />
                </div>
                <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                        iOS bundle ID
                    </p>
                    <input
                        data-testid={`fb-${app}-ios-bid`}
                        value={value.ios_bundle_id}
                        onChange={(e) => setField("ios_bundle_id", e.target.value.trim())}
                        placeholder={`com.acme.${app}`}
                        className="w-full px-2 py-1.5 text-xs border border-border rounded-md bg-card font-mono"
                    />
                </div>
            </div>

            <FileTextInput
                label={`${appLabel} app · Firebase Service Account JSON`}
                sublabel="Used to send FCM v1 push notifications AND auto-fetch the Android/iOS config files (when auto-fetch is enabled)."
                value={value.service_account_json}
                onChange={(v) => setField("service_account_json", v)}
                accept="application/json,.json"
                testid={`fb-${app}-sa-json`}
                rows={3}
            />

            <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                    App display name (optional)
                </p>
                <input
                    data-testid={`fb-${app}-app-name`}
                    value={value.mobile_app_name}
                    onChange={(e) => setField("mobile_app_name", e.target.value)}
                    placeholder={namePh}
                    className="w-full px-2 py-1.5 text-xs border border-border rounded-md bg-card"
                />
            </div>

            <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 rounded-lg p-2">
                <label className="inline-flex items-start gap-2 cursor-pointer" data-testid={`fb-${app}-autofetch-toggle`}>
                    <input
                        type="checkbox"
                        checked={value.auto_fetch !== false}
                        onChange={(e) => setField("auto_fetch", e.target.checked)}
                        className="mt-0.5"
                    />
                    <span className="flex-1">
                        <span className="text-[11px] font-bold inline-flex items-center gap-1">
                            <Sparkles size={11} className="text-indigo-700" />
                            Auto-fetch google-services.json &amp; GoogleService-Info.plist
                        </span>
                        <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                            When enabled (default), Bizil calls the Firebase Management API with the SA JSON above to create/find the Android + iOS apps in the project and download the config files automatically. Disable to upload them manually below.
                        </p>
                    </span>
                </label>
            </div>

            {!value.auto_fetch && (
                <div className="space-y-3 pl-2 border-l-2 border-amber-200" data-testid={`fb-${app}-manual-block`}>
                    <FileTextInput
                        label={`${appLabel} app · google-services.json`}
                        sublabel={`Must contain a client for package ${value.android_package_name || "<package-name>"}`}
                        value={value.google_services_json}
                        onChange={(v) => setField("google_services_json", v)}
                        accept="application/json,.json"
                        testid={`fb-${app}-gs`}
                    />
                    <FileTextInput
                        label={`${appLabel} app · GoogleService-Info.plist`}
                        sublabel={`BUNDLE_ID inside must equal ${value.ios_bundle_id || "<bundle-id>"}`}
                        value={value.ios_plist}
                        onChange={(v) => setField("ios_plist", v)}
                        accept=".plist,application/xml,text/xml"
                        testid={`fb-${app}-plist`}
                    />
                </div>
            )}
        </div>
    );
}


/** Aggregate status badge — shown on the panel header.
 *
 * iter146 — Multi-Firebase-project requirement: "when a tenant already
 * has a Firebase project assigned, clearly indicate that to the user."
 * The status payload already carries the resolved project id
 * (``status.project_id`` for EXISTING mode, ``status.auto_shard_project_id``
 * for AUTO mode via the shard pool) — it just wasn't surfaced anywhere
 * outside the collapsed detail rows. Appending it here means the super
 * admin sees WHICH Firebase project a tenant is on at a glance, without
 * expanding the panel. Purely additive to the label string — modes,
 * tones, and every existing ``data-testid`` are unchanged. */
export function FirebaseStatusBadge({ status }) {
    if (!status) return null;
    const { mode, configured } = status;
    const projectId = status.project_id || status.auto_shard_project_id || null;
    let label, tone, testidTone;
    if (mode === "EXISTING" && configured) {
        label = `Firebase · Existing project ✓${projectId ? ` · ${projectId}` : ""}`;
        tone = "bg-violet-100 text-violet-700 border-violet-200";
        testidTone = "existing";
    } else if (mode === "MIXED") {
        const parts = [];
        if (status.customer?.mode) parts.push(`C=${status.customer.mode}${status.customer?.project_id ? ` (${status.customer.project_id})` : ""}`);
        if (status.rider?.mode) parts.push(`R=${status.rider.mode}${status.rider?.project_id ? ` (${status.rider.project_id})` : ""}`);
        label = `Firebase · Mixed (${parts.join(", ")})`;
        tone = "bg-sky-100 text-sky-700 border-sky-200";
        testidTone = "mixed";
    } else if (mode === "AUTO" && configured) {
        label = `Firebase · Auto-provisioned ✓${projectId ? ` · ${projectId}` : ""}`;
        tone = "bg-emerald-100 text-emerald-700 border-emerald-200";
        testidTone = "auto";
    } else if ((status.slots_filled || 0) > 0) {
        label = `Firebase · ${mode} · ${status.slots_filled}/${status.slots_total}${projectId ? ` · ${projectId}` : ""}`;
        tone = "bg-amber-100 text-amber-700 border-amber-200";
        testidTone = "partial";
    } else {
        label = "Firebase · Not configured — will auto-pick next available project";
        tone = "bg-muted text-muted-foreground border-border";
        testidTone = "none";
    }
    return (
        <span
            data-testid={`fb-status-badge-${testidTone}`}
            className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-md border ${tone}`}
        >
            {label}
        </span>
    );
}


function ReadOnlyRow({ label, value, testid }) {
    return (
        <div className="grid grid-cols-3 gap-2 py-0.5">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                {label}
            </span>
            <span data-testid={testid} className="col-span-2 text-[11px] font-mono break-all">
                {value || <span className="text-muted-foreground italic">— not set —</span>}
            </span>
        </div>
    );
}


function ReadOnlyAppStatus({ appStatus, autoShardProjectId }) {
    /** Read-only per-app status block — shown on every tenant card. */
    const { app, mode, project_id, android_package_name, ios_bundle_id,
            mobile_app_name, has_google_services_json, has_ios_plist } = appStatus;
    const isCustomer = app === "customer";
    const Icon = isCustomer ? Smartphone : Bike;
    const effectiveProjectId = project_id || (mode === "AUTO" ? autoShardProjectId : null);
    return (
        <div
            data-testid={`fb-readonly-${app}`}
            className="bg-card border border-border rounded-lg p-2.5"
        >
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                <Icon size={11} /> {isCustomer ? "Customer app" : "Rider app"}
                <span className={`ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[9px] border ${mode === "EXISTING" ? "bg-violet-50 border-violet-200 text-violet-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}
                      data-testid={`fb-readonly-${app}-mode`}>
                    {mode}
                </span>
            </p>
            <ReadOnlyRow label="Project ID" value={effectiveProjectId} testid={`fb-readonly-${app}-project`} />
            <ReadOnlyRow label="Android App ID" value={android_package_name} testid={`fb-readonly-${app}-android`} />
            <ReadOnlyRow label="iOS Bundle ID" value={ios_bundle_id} testid={`fb-readonly-${app}-ios`} />
            <ReadOnlyRow label="Display name" value={mobile_app_name} testid={`fb-readonly-${app}-name`} />
            <ReadOnlyRow label="Configs"
                 value={`${has_google_services_json ? "✓ Android" : "✗ Android"}  ·  ${has_ios_plist ? "✓ iOS" : "✗ iOS"}`}
                 testid={`fb-readonly-${app}-configs`} />
        </div>
    );
}


/** Main panel — collapsible block in BrandingPanel-style. */
export default function TenantFirebasePanel({ tenant, onSaved }) {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [busyApp, setBusyApp] = useState(null);
    // Per-app draft state — keyed by app name.
    const [draft, setDraft] = useState({
        customer: { ...EMPTY_APP_EXISTING },
        rider: { ...EMPTY_APP_EXISTING },
    });
    const [editingApp, setEditingApp] = useState(null); // 'customer' / 'rider' / null
    const [errors, setErrors] = useState([]);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/super/tenants/${tenant.id}/firebase/status`);
            setStatus(data);
        } catch {/* silent */}
    };
    useEffect(() => { refresh(); }, [tenant.id]);

    const switchToAuto = async (app) => {
        const typed = window.prompt(
            `Switch the ${app.toUpperCase()} app of ${tenant.name} back to AUTO Firebase?\n\n` +
            `This clears that app's service account, project_id, and cached config files. The other app's configuration is untouched.\n\n` +
            `Type "SWITCH" to confirm:`
        );
        if (typed !== "SWITCH") { toast.info("Cancelled."); return; }
        setBusyApp(app);
        try {
            await api.post(`/super/tenants/${tenant.id}/firebase/mode/${app}`, { mode: "AUTO" });
            toast.success(`${app[0].toUpperCase()}${app.slice(1)} app switched to AUTO.`);
            setDraft({ ...draft, [app]: { ...EMPTY_APP_EXISTING } });
            setEditingApp(null);
            await refresh();
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Switch failed");
        } finally { setBusyApp(null); }
    };

    const autoProvision = async (app) => {
        /** Trigger the shared-shard auto-provisioning flow for the tenant.
         *  The existing /firebase/provision endpoint provisions both apps
         *  but iter74 made it per-app-aware: EXISTING apps are skipped,
         *  AUTO apps go through normal shard-based create/download.
         *  We report per-app result so the user knows what changed.
         */
        setBusyApp(app);
        try {
            const { data } = await api.post(`/super/tenants/${tenant.id}/firebase/provision`);
            // Report what actually got provisioned. Surface the per-app
            // skip reasons so the user knows the EXISTING app wasn't
            // touched.
            const parts = [];
            if (data.customer_skipped) parts.push("customer: skipped (EXISTING)");
            else if (data.customer_android) parts.push("customer: provisioned ✓");
            if (data.rider_skipped) parts.push("rider: skipped (EXISTING)");
            else if (data.rider_android) parts.push("rider: provisioned ✓");
            toast.success(parts.length ? parts.join(" · ") : "Auto-provision done");
            await refresh();
            onSaved?.();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Auto-provision failed";
            // Hint: most common cause is "no shard configured".
            if (/shard/i.test(detail)) {
                toast.error(
                    `${detail} — visit Super Admin → Firebase to add a shard.`,
                    { duration: 8000 },
                );
            } else {
                toast.error(detail);
            }
        } finally { setBusyApp(null); }
    };

    const saveExisting = async (app) => {
        const form = draft[app];
        const errs = clientValidateApp(form);
        setErrors(errs);
        if (errs.length > 0) {
            toast.error(`Fix ${errs.length} issue(s) before saving`);
            return;
        }
        const appStatus = status?.[app];
        const alreadyConfigured = appStatus?.mode === "EXISTING" && appStatus.configured;
        if (alreadyConfigured) {
            const typed = window.prompt(
                `Replace the EXISTING Firebase configuration for the ${app.toUpperCase()} app of ${tenant.name}?\n\n` +
                `This will overwrite the service account, App ID, bundle ID, and cached config files for this app.\n\n` +
                `Type "SWITCH" to confirm:`
            );
            if (typed !== "SWITCH") { toast.info("Cancelled."); return; }
        }
        setBusyApp(app);
        try {
            const { data } = await api.post(
                `/super/tenants/${tenant.id}/firebase/existing/${app}`, form,
            );
            toast.success(
                data.auto_fetched
                    ? `${app[0].toUpperCase()}${app.slice(1)} configured · project ${data.project_id} · configs auto-fetched ✨`
                    : `${app[0].toUpperCase()}${app.slice(1)} configured · project ${data.project_id}`
            );
            setDraft({ ...draft, [app]: { ...EMPTY_APP_EXISTING } });
            setEditingApp(null);
            await refresh();
            onSaved?.();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Save failed";
            setErrors([detail]);
            toast.error(detail);
        } finally { setBusyApp(null); }
    };

    const showEditor = (app) => {
        setEditingApp(app);
        setErrors([]);
    };

    return (
        <div className="mt-3 pt-3 border-t border-border" data-testid={`firebase-panel-${tenant.slug}`}>
            <button
                data-testid={`firebase-panel-${tenant.slug}-toggle`}
                onClick={() => setOpen((x) => !x)}
                className="w-full text-left flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground"
            >
                <span className="inline-flex items-center gap-1.5">
                    <Flame size={11} /> Firebase configuration
                </span>
                <span className="inline-flex items-center gap-2">
                    <FirebaseStatusBadge status={status} />
                    <span>{open ? "▾" : "▸"}</span>
                </span>
            </button>
            {open && (
                <div className="mt-3 space-y-3 text-xs">
                    {/* Per-app read-only status — Tenant Details view. */}
                    {status && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <ReadOnlyAppStatus
                                appStatus={status.customer}
                                autoShardProjectId={status.auto_shard_project_id}
                            />
                            <ReadOnlyAppStatus
                                appStatus={status.rider}
                                autoShardProjectId={status.auto_shard_project_id}
                            />
                        </div>
                    )}

                    {/* Per-app action buttons */}
                    {!editingApp && (
                        <div className="grid grid-cols-2 gap-2">
                            {["customer", "rider"].map((app) => {
                                const appStatus = status?.[app];
                                const appMode = appStatus?.mode || "AUTO";
                                const isProvisioned = appStatus?.configured;
                                return (
                                    <div key={app}
                                         className="border border-border rounded-lg p-2 space-y-1.5"
                                         data-testid={`fb-actions-${app}`}>
                                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                            {app === "customer" ? "Customer" : "Rider"} app actions
                                        </p>
                                        {/* iter75 — When in AUTO mode, the discoverable entry
                                            point is "Auto-provision now". For EXISTING mode the
                                            primary action is replacing the existing config. */}
                                        {appMode === "AUTO" && (
                                            <button
                                                onClick={() => autoProvision(app)}
                                                disabled={busyApp === app}
                                                data-testid={`fb-autoprovision-${app}`}
                                                className="w-full text-[11px] font-bold px-2 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center justify-center gap-1 disabled:opacity-50"
                                            >
                                                {busyApp === app && <Loader2 size={10} className="animate-spin" />}
                                                <Sparkles size={11} />
                                                {isProvisioned ? "Re-provision (refresh configs)" : "Auto-provision now"}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => showEditor(app)}
                                            data-testid={`fb-edit-${app}`}
                                            className="w-full text-[11px] font-bold px-2 py-1.5 rounded bg-foreground text-background hover:opacity-80"
                                        >
                                            {appMode === "EXISTING" ? "Replace existing config" : "Switch to existing Firebase project"}
                                        </button>
                                        {appMode === "EXISTING" && (
                                            <button
                                                onClick={() => switchToAuto(app)}
                                                disabled={busyApp === app}
                                                data-testid={`fb-reset-${app}`}
                                                className="w-full text-[10px] px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-50"
                                            >
                                                {busyApp === app && <Loader2 size={10} className="inline animate-spin mr-1" />}
                                                Reset to AUTO (shared shard)
                                            </button>
                                        )}
                                        {appMode === "AUTO" && !isProvisioned && (
                                            <p className="text-[9px] text-muted-foreground italic leading-snug">
                                                AUTO mode uses the shared Firebase shard pool. Clicking &quot;Auto-provision now&quot; creates the Android + iOS apps on an available shard and downloads the config files.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Per-app editor */}
                    {editingApp && (
                        <div className="border border-foreground/30 rounded-lg p-3 space-y-3 bg-muted/10"
                             data-testid={`fb-editor-${editingApp}`}>
                            <div className="flex items-center justify-between">
                                <p className="text-[11px] font-bold inline-flex items-center gap-1.5">
                                    {editingApp === "customer" ? <Smartphone size={12} /> : <Bike size={12} />}
                                    Configure existing Firebase for {editingApp.toUpperCase()} app
                                </p>
                                <button
                                    onClick={() => { setEditingApp(null); setErrors([]); }}
                                    data-testid={`fb-editor-${editingApp}-cancel`}
                                    className="text-[10px] text-muted-foreground hover:text-foreground"
                                >
                                    <X size={12} className="inline" /> Cancel
                                </button>
                            </div>
                            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-2 inline-flex items-start gap-1.5 w-full">
                                <ShieldAlert size={12} className="text-amber-700 flex-shrink-0 mt-0.5" />
                                <p className="text-[10px] text-amber-900 dark:text-amber-200 leading-snug">
                                    This configures ONLY the {editingApp} app. The other app&apos;s Firebase configuration is unchanged. When auto-fetch is enabled (default), Bizil calls the Firebase Management API with the Service Account to create/find the Android + iOS apps in the project and download the config files automatically.
                                </p>
                            </div>
                            <FirebaseAppExistingForm
                                value={draft[editingApp]}
                                onChange={(v) => setDraft({ ...draft, [editingApp]: v })}
                                app={editingApp}
                                tenant={tenant}
                            />
                            {errors.length > 0 && (
                                <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 rounded-lg p-2"
                                     data-testid={`fb-errors-${editingApp}`}>
                                    {errors.map((e, i) => (
                                        <p key={i} className="text-[10px] text-rose-700 leading-snug inline-flex items-start gap-1">
                                            <X size={10} className="flex-shrink-0 mt-0.5" /> {e}
                                        </p>
                                    ))}
                                </div>
                            )}
                            <button
                                data-testid={`fb-save-${editingApp}`}
                                disabled={busyApp === editingApp}
                                onClick={() => saveExisting(editingApp)}
                                className="w-full bg-foreground text-background rounded-lg py-2 text-[11px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
                            >
                                {busyApp === editingApp ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                                {draft[editingApp].auto_fetch !== false
                                    ? `Validate, auto-fetch configs & save (${editingApp})`
                                    : `Validate & save manual config (${editingApp})`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
